import { type Element, Group, Text, Image as ZImage } from 'zrender'

/**
 * Create an image for a tile.
 */
export function createImage(
    src: string,
    cb?: (img: ZImage, image: HTMLImageElement) => void
) {
    const image = new Image()
    const img = new ZImage({
        style: {
            image,
            width: 1,
            height: 1
        }
    })

    image.onload = () => {
        cb?.(img, image)
    }

    image.src = src

    if (image.complete) {
        cb?.(img, image)
    }

    return img
}

/**
 * Create a text element for a tile.
 */
export function createText(
    text: string,
    size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'md',
    width: number | undefined = undefined
) {
    switch (size) {
        case 'lg':
            return createLargeText(text, width)
        case 'xl':
            return createExtraLargeText(text, width)
        case 'sm':
            return createSmallText(text, width)
        case 'md':
        default:
            return createNormalText(text, width)
    }
}

/**
 * Create an extra large text element for a tile.
 */
export function createExtraLargeText(text: string, width?: number) {
    return new Text({
        style: {
            text: text.toUpperCase(),
            fill: 'black',
            fontFamily: 'Rubik',
            fontWeight: 'bold',
            fontSize: 38,
            lineHeight: 40,
            overflow: 'break',
            lineOverflow: 'truncate',
            width
        }
    })
}

/**
 * Create a large text element for a tile.
 */
export function createLargeText(text: string, width?: number) {
    return new Text({
        style: {
            text: text,
            fill: 'black',
            fontFamily: 'Source Sans Pro',
            fontWeight: 'bold',
            fontSize: 16,
            lineHeight: 17,
            overflow: 'break',
            lineOverflow: 'truncate',
            width
        }
    })
}

/**
 * Create a normal text element for a tile.
 */
export function createNormalText(text: string, width?: number) {
    return new Text({
        style: {
            text: text,
            fill: 'black',
            fontFamily: 'Source Sans Pro',
            fontSize: 13,
            lineHeight: 14,
            overflow: 'break',
            lineOverflow: 'truncate',
            width
        }
    })
}

/**
 * Create a small text element for a tile.
 */
export function createSmallText(text: string, width?: number) {
    return new Text({
        style: {
            text: text,
            fill: 'black',
            fontFamily: 'Source Sans Pro',
            fontSize: 10,
            lineHeight: 11,
            overflow: 'break',
            lineOverflow: 'truncate',
            width
        }
    })
}

/**
 *  Create a bullet icon for a tile.
 */
export function createBulletIcon(icon: string, textEl: Text) {
    const img = createImage(icon)
    const height = Number(textEl.style.fontSize)
    const width = img.getWidth()
    img.setStyle({ height, width })

    const textHeight = textEl.getBoundingRect().height

    if (textHeight > height) {
        img.y = (textHeight - height) / 2
    }

    return img
}

/**
 * Create a bullet element for a tile.
 */
export function createBullet(
    text: string,
    index: number,
    bullet: string | BulletCreator,
    item: Text
) {
    return typeof bullet === 'string'
        ? createBulletIcon(bullet, item)
        : bullet(text, index, item)
}

/**
 * Create a list item element for a tile.
 */
export function createListItem(
    text: string,
    index: number,
    bullet?: string | BulletCreator,
    width?: number
) {
    const group = new Group()

    let x = 0

    const item = createNormalText(text)

    if (bullet) {
        const element = createBullet(text, index, bullet, item)
        group.add(element)
        x += element.getBoundingRect().width + 5
    }

    const height = item.getBoundingRect().height
    item.x = x
    item.setStyle({ width, height })
    group.add(item)

    return group
}

/**
 * Create a list element for a tile.
 */
export function createList(
    items: string[],
    bullet?: string | BulletCreator,
    width?: number
) {
    const group = new Group()

    let y = 0

    for (let i = 0; i < items.length; i++) {
        const item = items[i]
        const element = createListItem(item, i, bullet, width)
        element.y = y
        group.add(element)
        y += element.getBoundingRect().height + 5
    }

    return group
}

type BulletCreator = (item: string, index: number, textEl: Text) => Element
